import { FeedItem } from 'types/feeds';

export const DOUBLE_COLON = '::';

export type DoubleColonFilterOption = {
  /** The full tag label, e.g. "Audience::Residents". The option's identity, so two groups can
   *  share a value without cross-matching. */
  tag: string;
  /** The part after the first delimiter — what the resident actually reads. */
  value: string;
};

export type DoubleColonFilterGroup = {
  /** The part before the first delimiter, used as the dropdown's heading. */
  key: string;
  options: DoubleColonFilterOption[];
};

/** Selected tag per group key; null means "Any". */
export type DoubleColonSelection = Record<string, string | null>;

/**
 * Splits a tag label on its FIRST delimiter, so a value may itself contain one:
 * "Audience::A::B" is group "Audience", value "A::B".
 *
 * Returns null when the label carries no delimiter, or when either side is blank once trimmed —
 * an index of 0 means the label opens with the delimiter and so has no group.
 */
export function parseDoubleColonTag(tag: string): { key: string; value: string } | null {
  const index = tag.indexOf(DOUBLE_COLON);
  if (index <= 0) return null;

  const key = tag.slice(0, index).trim();
  const value = tag.slice(index + DOUBLE_COLON.length).trim();
  if (!key || !value) return null;

  return { key, value };
}

/**
 * Derives the dropdowns from the tags already on the loaded feed items. There is no configured
 * group list — the block only carries a boolean, so whatever groups the data holds are what
 * residents see. Options are deduplicated by full tag label and sorted for a stable render.
 */
export function buildDoubleColonFilterGroups(items: FeedItem[], locale?: string): DoubleColonFilterGroup[] {
  const groups = new Map<string, Map<string, string>>();

  items.forEach((item) => {
    (item.tags ?? []).forEach((tag) => {
      const parsed = parseDoubleColonTag(tag);
      if (!parsed) return;

      const options = groups.get(parsed.key) ?? new Map<string, string>();
      options.set(tag, parsed.value);
      groups.set(parsed.key, options);
    });
  });

  const compare = (first: string, second: string) => first.localeCompare(second, locale);

  return Array.from(groups.entries())
    .map(([key, options]) => ({
      key,
      options: Array.from(options.entries())
        .map(([tag, value]) => ({ tag, value }))
        .sort((first, second) => compare(first.value, second.value)),
    }))
    .sort((first, second) => compare(first.key, second.key));
}

/**
 * AND across groups: an item must carry every selected tag. An item with no value for an active
 * group therefore drops out, which is how Commonplace behaves.
 */
export function matchesDoubleColonSelection(item: FeedItem, selection: DoubleColonSelection): boolean {
  return Object.values(selection).every((tag) => !tag || (item.tags ?? []).includes(tag));
}

/**
 * Whether anything in the filter row is narrowing the feed — a dropdown selection or the keyword.
 * Drives the Clear control and the filtered-to-zero empty state, so both must see the keyword:
 * a non-matching keyword with every dropdown on "Any" is still a filter the resident needs a way
 * out of.
 */
export function hasActiveFilters(selection: DoubleColonSelection, searchValue = ''): boolean {
  return Boolean(searchValue) || Object.values(selection).some(Boolean);
}
